import React, { useEffect, useMemo, useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { getChartConfig } from '../utils/gameLogChartConfig';
import { getTeamLogoUrl } from '../utils/formatters';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

// Shared per-game performance chart used by the sport-specific player modals.
// Fetches season stats for the player, plots a bar per game (green = hit the
// betting line, red = miss), draws horizontal reference lines for the line
// itself and the season average, and can render config-driven overlays either
// as line datasets on the same chart or as a stacked mini bar chart below.
//
// Props:
//   sport         'nfl' | 'nhl' | 'nba' | 'wnba'
//   playerId      Raw player id ("nfl_12345" or "12345"); the "<sport>_" prefix
//                 is stripped for the API call.
//   marketKey     Selected market (e.g. 'player_pass_yds').
//   lineValue     Betting line the user is looking at (used to colour bars).
//                 Falls back to null → all bars neutral.
//   opponentTeam  Opposing team abbrev — enables the Vs Opponent toggle.
//   playerTeam    Player's team abbrev — used to compute home/away label.
//   year          Optional season override; defaults to the current year.
//   height        Primary chart pixel height (mobile-friendly default).
//   showTitle     Whether to render the "Recent Games" header line.
const PlayerGameLogChart = ({
  sport,
  playerId,
  marketKey,
  lineValue = null,
  opponentTeam = null,
  playerTeam = null,
  year,
  height = 260,
  showTitle = true,
}) => {
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDNPs, setShowDNPs] = useState(false);
  const [showVsOpponent, setShowVsOpponent] = useState(false);
  // Which overlays the user has toggled on for each config-driven overlay
  // (indexed by overlay.field). Reset when market changes.
  const [enabledOverlays, setEnabledOverlays] = useState({});

  const config = getChartConfig(sport, marketKey);
  const currentYear = String(new Date().getFullYear());
  const previousYear = String(new Date().getFullYear() - 1);
  // Selected season, initialized from the `year` prop (if any) or the current
  // year. Kept in state so the built-in year toggle can flip between the
  // current and previous season without re-mounting the component.
  const [selectedYear, setSelectedYear] = useState(year || currentYear);
  const effectiveYear = selectedYear;

  // Reset overlay picks whenever market or player changes — a prior market's
  // enabled-overlay flags rarely make sense for a different stat.
  useEffect(() => {
    setEnabledOverlays({});
  }, [marketKey, playerId]);

  useEffect(() => {
    if (!sport || !playerId) return;
    let cancelled = false;
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const cleanId = String(playerId).replace(new RegExp(`^${sport}_`), '');
        const res = await fetch(`/api/stats/${sport}/${effectiveYear}/${cleanId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setStatsData(data);
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setStatsData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStats();
    return () => { cancelled = true; };
  }, [sport, playerId, effectiveYear]);

  // Accessor helper — the primary and overlay fields can each be either a
  // gameLog field name string or a function that computes from a game row.
  const readValue = (accessor, game) => {
    if (typeof accessor === 'function') return Number(accessor(game)) || 0;
    if (typeof accessor === 'string') return Number(game[accessor]) || 0;
    return 0;
  };

  // Heuristic: a game with 0/null time-on-field or 0 minutes is a DNP. Kept
  // generic so different sports' gameLog shapes work without a per-sport
  // detector — this is imperfect but errs toward "not DNP" to avoid over-
  // filtering. Sport-specific overrides can be added later.
  const isDNP = (game) => {
    if (game.dnp === true) return true;
    if (game.status && String(game.status).toLowerCase() === 'dnp') return true;
    const toi = game.timeOnIce ?? game.minutes ?? null;
    if (toi === null || toi === undefined) return false;
    if (typeof toi === 'number') return toi === 0;
    return toi === '0:00' || toi === '00:00' || toi === '0';
  };

  const prepared = useMemo(() => {
    const games = statsData?.gameLog;
    if (!games || games.length === 0 || !config) return null;

    // Home/away team abbrevs on the gameLog can live under `homeTeam`
    // (NHL/NBA/WNBA shape) or `homeTeamAbbrev` (NFL shape). Prefer the
    // explicit abbrev field, fall back to the shorter name.
    const homeOf = (g) => g.homeTeamAbbrev || g.homeTeam || null;
    const awayOf = (g) => g.awayTeamAbbrev || g.awayTeam || null;

    let filtered = [...games];
    if (!showDNPs) filtered = filtered.filter(g => !isDNP(g));
    if (showVsOpponent && opponentTeam) {
      filtered = filtered.filter(g => awayOf(g) === opponentTeam || homeOf(g) === opponentTeam);
    }
    // Most-recent first
    filtered.sort((a, b) => new Date(b.gameDate) - new Date(a.gameDate));

    if (filtered.length === 0) return { empty: true };

    // X-axis label is date-only now; the opponent's team logo (with @/vs)
    // is rendered in a separate HTML row below the chart.
    const labels = filtered.map(game => {
      const d = new Date(`${game.gameDate}T12:00:00-05:00`);
      return `${d.getMonth() + 1}/${d.getDate()}`;
    });
    const opponents = filtered.map(game => {
      const home = homeOf(game);
      const away = awayOf(game);
      if (!playerTeam) return { abbrev: game.opponent || null, isHome: true };
      const isHome = home === playerTeam;
      return { abbrev: isHome ? away : home, isHome };
    });

    const rawPrimary = filtered.map(g => readValue(config.primary, g));
    // Binary markets (anytime TD, first TD, DD/TD, etc.) render as
    // hit=Yes bars (tall, green) vs miss=No slivers (short, red) — matches
    // the NBA modal's convention. Numeric markets keep raw heights with a
    // 0.25 sliver only when the value is exactly 0.
    const barData = rawPrimary.map(v => {
      if (config.isBinary) return v >= 1 ? 2 : 0.25;
      return v === 0 ? 0.25 : v;
    });

    const gamesPlayedCount = games.filter(g => !isDNP(g)).length;
    const total = rawPrimary.reduce((s, v) => s + v, 0);
    const avg = filtered.length > 0 ? total / filtered.length : 0;
    const hits = lineValue != null ? rawPrimary.filter(v => v >= lineValue).length : null;

    // Overlay datasets (only for enabled overlays; split by layout kind).
    const sameChartOverlays = [];
    const stackedOverlays = [];
    (config.overlays || []).forEach(ov => {
      if (!enabledOverlays[ov.field]) return;
      const values = filtered.map(g => readValue(ov.field, g));
      const entry = { ...ov, values };
      if (ov.layout === 'stacked') stackedOverlays.push(entry);
      else sameChartOverlays.push(entry);
    });

    return {
      labels,
      opponents,
      rawPrimary,
      barData,
      avg,
      hits,
      gamesPlayedCount,
      totalShown: filtered.length,
      sameChartOverlays,
      stackedOverlays,
      // Raw game log entries per bar — used by the tooltip to surface
      // companion stat fields on hover (e.g. Rec/Tgt/TD when viewing Rec Yds).
      games: filtered,
    };
  }, [statsData, config, showDNPs, showVsOpponent, opponentTeam, playerTeam, enabledOverlays, lineValue]);

  if (!config) return null;

  const containerScrollStyle = { overflowX: 'auto', overflowY: 'hidden' };
  const canvasWidthStyle = (labelCount) => ({
    height: `${height}px`,
    minWidth: `${Math.max(labelCount * 40, 300)}px`,
  });

  const chartTitle = config.isBinary
    ? (config.isFirstScorer ? 'First Scorer' : 'Anytime')
    : 'Recent Games';

  const buildPrimaryChartData = () => {
    if (!prepared || prepared.empty) return null;
    const { barData, rawPrimary, labels, sameChartOverlays, avg } = prepared;

    const barColors = barData.map((_, idx) => {
      const actual = rawPrimary[idx];
      if (config.isBinary) {
        // Green when the player hit the market, red sliver when they didn't.
        return actual >= 1 ? 'rgba(34, 197, 94, 0.85)' : 'rgba(239, 68, 68, 0.8)';
      }
      // Green above season average, red below. Falls back to the betting
      // line when the average is 0 and a line is provided.
      const threshold = avg > 0 ? avg : lineValue;
      if (threshold == null) return 'rgba(59, 130, 246, 0.7)';
      return actual >= threshold ? 'rgba(34, 197, 94, 0.85)' : 'rgba(239, 68, 68, 0.8)';
    });

    // Prefer the config's explicit `label` (e.g. "Rush Yds") so the tooltip
    // and legend name the actual stat rather than the generic "Recent Games"
    // header. Falls back to the header title when a config didn't set one.
    const primaryLabel = config.label || chartTitle;
    const datasets = [{
      type: 'bar',
      label: primaryLabel,
      data: barData,
      backgroundColor: barColors,
      borderColor: barColors.map(c => c.replace(/,\s*[\d.]+\)$/, ', 1)')),
      borderWidth: 1,
      // Fixed bar width — matches the NBA/WNBA modals so a single-game
      // chart doesn't stretch the bar to full width and multi-game charts
      // stay predictable.
      barThickness: 30,
      order: 2,
    }];

    if (lineValue != null && !config.isBinary) {
      datasets.push({
        type: 'line',
        label: `Line (${lineValue})`,
        data: Array(barData.length).fill(lineValue),
        borderColor: 'rgba(59, 130, 246, 0.9)',
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
        fill: false,
        order: 1,
      });
    }
    if (!config.isBinary && barData.length > 0) {
      datasets.push({
        type: 'line',
        label: `Avg (${avg.toFixed(1)})`,
        data: Array(barData.length).fill(avg),
        borderColor: 'rgba(251, 191, 36, 0.9)',
        borderWidth: 2,
        borderDash: [3, 3],
        pointRadius: 0,
        fill: false,
        order: 1,
      });
    }
    // Same-chart overlays render as background bar datasets on the same
    // axes — matches the NHL modal's Goals view where "Shots on Goal" sits
    // behind the goal bars. Bar heights use the raw overlay value with a
    // sliver replacement for zeros so the bar is still visible.
    sameChartOverlays.forEach(ov => {
      const barValues = ov.values.map(v => v === 0 ? 0.25 : v);
      datasets.push({
        type: 'bar',
        label: ov.label,
        data: barValues,
        backgroundColor: ov.color || 'rgba(148, 163, 184, 0.4)',
        borderColor: (ov.color || 'rgba(148, 163, 184, 0.6)').replace(/,\s*[\d.]+\)$/, ', 0.7)'),
        borderWidth: 1,
        barThickness: 30,
        order: 3, // Render behind the primary bars
      });
    });

    return { labels, datasets };
  };

  const buildStackedChartData = (ov) => {
    if (!prepared || prepared.empty) return null;
    const barValues = ov.values.map(v => v === 0 ? 0.25 : v);
    return {
      labels: prepared.labels,
      datasets: [{
        type: 'bar',
        label: ov.label,
        data: barValues,
        backgroundColor: ov.color || 'rgba(148, 163, 184, 0.6)',
        borderColor: (ov.color || 'rgba(148, 163, 184, 0.9)').replace(/,\s*[\d.]+\)$/, ', 1)'),
        borderWidth: 1,
        barThickness: 30,
      }],
    };
  };

  const primaryYOptions = () => {
    // All accumulation stats are whole numbers (yards, TDs, INTs,
    // receptions, etc.). Suppress any fractional tick Chart.js proposes
    // — the callback returns '' for non-integer values so only whole
    // numbers ever render on the axis.
    return {
      beginAtZero: true,
      max: config.isBinary ? 2.5 : undefined,
      ticks: {
        color: '#94a3b8',
        stepSize: config.isBinary ? 1 : undefined,
        precision: 0,
        callback: (v) => {
          if (config.isBinary) {
            if (v === 2) return 'Yes';
            if (v === 0) return 'No';
            return '';
          }
          return Number.isInteger(v) ? v : '';
        },
      },
      grid: { color: 'rgba(148, 163, 184, 0.1)' },
    };
  };

  const commonChartOptions = (yOptions) => ({
    responsive: true,
    maintainAspectRatio: false,
    // Right padding + fixed y-axis width so the HTML logo row below
    // can line up its columns with the bar centers.
    layout: { padding: { right: 8 } },
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        display: true,
        // Matches NBA modal — bottom-anchored legend, showing only the Line
        // and Avg reference chips (bar / overlay datasets are hidden).
        position: 'bottom',
        labels: {
          color: '#94a3b8',
          usePointStyle: true,
          padding: 15,
          font: { size: 11 },
          filter: (item) => /Line|Avg/.test(item.text || ''),
        },
      },
      tooltip: {
        // Primary bar first, then overlay lines (Avg / Line / same-chart
        // overlays). Default Chart.js ordering was surfacing the reference
        // line datasets before the actual stat.
        itemSort: (a, b) => {
          const aType = a.dataset?.type === 'bar' ? 0 : 1;
          const bType = b.dataset?.type === 'bar' ? 0 : 1;
          if (aType !== bType) return aType - bType;
          return a.datasetIndex - b.datasetIndex;
        },
        callbacks: {
          label: (ctx) => {
            if (ctx.dataset.type === 'line') {
              // Avg / Line values — always render to 1 decimal so the
              // tooltip stays tidy (avoids "8.235294117647..." from raw
              // averages).
              const v = Number(ctx.parsed.y);
              const formatted = Number.isFinite(v) ? v.toFixed(1) : ctx.parsed.y;
              return `${ctx.dataset.label}: ${formatted}`;
            }
            // Primary bar — show the raw stat (not the transformed height /
            // sliver) plus the game's companion stats from config.
            const idx = ctx.dataIndex;
            const raw = prepared?.rawPrimary?.[idx] ?? ctx.parsed.y;
            const lines = [
              config.isBinary
                ? `${ctx.dataset.label}: ${raw >= 1 ? 'Yes' : 'No'}`
                : `${ctx.dataset.label}: ${raw}`,
            ];
            const game = prepared?.games?.[idx];
            if (game && Array.isArray(config.companions)) {
              config.companions.forEach(({ field, label }) => {
                const v = game[field];
                if (v === null || v === undefined) return;
                lines.push(`${label}: ${v}`);
              });
            }
            return lines;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: '#94a3b8', font: { size: 10 }, maxRotation: 0, autoSkip: false },
        grid: { color: 'rgba(148, 163, 184, 0.06)' },
      },
      y: {
        ...yOptions,
        // Lock y-axis pixel width so the HTML logo row can pad-align.
        afterFit: (scale) => { scale.width = 32; },
      },
    },
  });

  const toggleOverlay = (field) => {
    setEnabledOverlays(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const overlayToggleChip = (ov) => {
    const on = !!enabledOverlays[ov.field];
    return (
      <button
        key={ov.field}
        type="button"
        onClick={() => toggleOverlay(ov.field)}
        style={{
          padding: '3px 10px',
          fontSize: '0.7rem',
          fontWeight: 600,
          borderRadius: '4px',
          border: 'none',
          backgroundColor: on ? '#0ea5e9' : '#334155',
          color: on ? '#fff' : '#94a3b8',
          cursor: 'pointer',
        }}
      >
        {ov.label}
      </button>
    );
  };

  const primaryChartData = buildPrimaryChartData();

  return (
    <div style={{ padding: '8px 4px' }}>
      {showTitle && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ color: '#cbd5e1', fontSize: '0.85rem', fontWeight: 600 }}>
            {chartTitle}
            {prepared && !prepared.empty && (
              <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: '8px', fontSize: '0.75rem' }}>
                {config.isBinary
                  ? `${prepared.hits ?? prepared.rawPrimary.filter(v => v >= 1).length}/${prepared.totalShown}`
                  : `Avg ${prepared.avg.toFixed(1)}${prepared.hits != null ? ` · ${prepared.hits}/${prepared.totalShown} hits` : ''}`}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Season toggle — current vs previous. Matches WNBA modal's UI. */}
            <div style={{ display: 'flex', gap: '2px' }}>
              {[currentYear, previousYear].map(yr => (
                <button
                  key={yr}
                  type="button"
                  onClick={() => setSelectedYear(yr)}
                  style={{
                    padding: '3px 8px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: selectedYear === yr ? '#f59e0b' : '#334155',
                    color: selectedYear === yr ? '#0f172a' : '#94a3b8',
                    cursor: 'pointer',
                  }}
                >
                  {yr}
                </button>
              ))}
            </div>
            {(config.overlays || []).map(overlayToggleChip)}
            {/* iOS-style slider toggles — same shape as NBAPlayerModal. */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.75rem', color: '#94a3b8' }}>
              <div
                onClick={() => setShowDNPs(v => !v)}
                style={{
                  width: '36px',
                  height: '20px',
                  borderRadius: '10px',
                  backgroundColor: showDNPs ? '#f59e0b' : '#4b5563',
                  position: 'relative',
                  transition: 'background-color 0.2s',
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  position: 'absolute',
                  top: '2px',
                  left: showDNPs ? '18px' : '2px',
                  transition: 'left 0.2s',
                }} />
              </div>
              DNPs
            </label>
            {opponentTeam && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.75rem', color: '#94a3b8' }}>
                <div
                  onClick={() => setShowVsOpponent(v => !v)}
                  style={{
                    width: '36px',
                    height: '20px',
                    borderRadius: '10px',
                    backgroundColor: showVsOpponent ? '#10b981' : '#4b5563',
                    position: 'relative',
                    transition: 'background-color 0.2s',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    position: 'absolute',
                    top: '2px',
                    left: showVsOpponent ? '18px' : '2px',
                    transition: 'left 0.2s',
                  }} />
                </div>
                vs {opponentTeam}
              </label>
            )}
          </div>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <Spinner animation="border" size="sm" variant="warning" />
        </div>
      )}
      {!loading && error && (
        <div style={{ color: '#f87171', fontSize: '0.8rem', padding: '12px 0', textAlign: 'center' }}>
          Could not load stats.
        </div>
      )}
      {!loading && !error && (!prepared || prepared.empty) && (
        <div style={{ color: '#6b7280', fontSize: '0.8rem', padding: '12px 0', textAlign: 'center' }}>
          No game log available.
        </div>
      )}

      {!loading && primaryChartData && (
        <>
          <div className="dark-scrollbar" style={containerScrollStyle}>
            <div style={canvasWidthStyle(primaryChartData.labels.length)}>
              <Bar data={primaryChartData} options={commonChartOptions(primaryYOptions())} />
            </div>
            {/* Opponent logo + @/vs row under the bars. Padding on left/right
                matches the y-axis width (32px) and layout.padding.right (8px)
                from commonChartOptions so columns line up with bar centers. */}
            {prepared.opponents && prepared.opponents.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${prepared.opponents.length}, 1fr)`,
                  paddingLeft: '32px',
                  paddingRight: '8px',
                  marginTop: '2px',
                  minWidth: `${Math.max(prepared.opponents.length * 40, 300)}px`,
                }}
              >
                {prepared.opponents.map((opp, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                    <span style={{ color: '#6b7280', fontSize: '0.6rem', lineHeight: 1 }}>
                      {opp.isHome ? 'vs' : '@'}
                    </span>
                    {opp.abbrev ? (
                      <img
                        src={getTeamLogoUrl(opp.abbrev, false, sport)}
                        alt={opp.abbrev}
                        title={opp.abbrev}
                        style={{ width: 18, height: 18, objectFit: 'contain' }}
                        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>

          {prepared.stackedOverlays.map(ov => (
            <div key={ov.field} style={{ marginTop: '14px' }}>
              <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginBottom: '4px' }}>{ov.label}</div>
              <div className="dark-scrollbar" style={containerScrollStyle}>
                <div style={{ height: '110px', minWidth: `${Math.max(prepared.labels.length * 40, 300)}px` }}>
                  <Bar
                    data={buildStackedChartData(ov)}
                    options={commonChartOptions({
                      beginAtZero: true,
                      ticks: { color: '#94a3b8' },
                      grid: { color: 'rgba(148, 163, 184, 0.08)' },
                    })}
                  />
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default PlayerGameLogChart;
